import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  createLocalStore,
  createLocalStoreFile,
  deleteLocalStore,
  deleteLocalStoreFile,
  getLocalStoreFiles,
  getLocalStores,
  updateLocalStore,
} from "../../../../services/api/instances/vectorStoreApi/methods";
import type {
  TStoreData,
  TStoreFile,
} from "../../../../services/api/instances/vectorStoreApi/types";
import i18n from "../../../../services/i18n";
import type { RootState } from "../../../../services/redux/types";
import { setSelectedStore, setStoreAction } from "../slice";
import { getUpdatedStores } from "../../helpers";
import { getStoreFilesAction } from "./openai_store_actions";
import { getError } from "../../../../services/lib/getError";
import { getFileByHash } from "../../../../services/api/instances/saveFileApi/methods";

export const getLocalStoresAction = createAsyncThunk(
  "database/getLocalStores",
  async (_, { rejectWithValue }) => {
    try {
      const responseStores = await getLocalStores();

      const stores: TStoreData[] = responseStores.data.map((store) => ({
        id: store.id,
        name: store.name,
        status: store.status,
        files: [],
        metadata: {
          type: "local",
          internal_id: store.id,
        },
      }));
      return stores;
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
  {
    condition(_, { getState }) {
      const state = getState() as RootState;
      const { hasMutation } = state.databaseState;

      if (!hasMutation) return false;

      return true;
    },
  },
);

export const createLocalStoreAction = createAsyncThunk(
  "database/createLocalStore",
  async (name: string, ThunkAPI) => {
    try {
      const { dataStore } = (ThunkAPI.getState() as RootState).databaseState;
      const initialName =
        name === ""
          ? `${i18n.t("knowledge_base.default_name")} ${dataStore.length + 1}`
          : name;
      const postObj = {
        name: initialName,
        metadata: {
          type: "local",
          internal_id: "",
        },
      };

      const store = await createLocalStore(postObj);
      const newStore: TStoreData = {
        id: store.id,
        name: initialName,
        status: store.status,
        files: [],
        metadata: {
          type: "local",
          internal_id: store.id,
        },
      };
      await updateLocalStore(store.id, {
        name: initialName,
        metadata: {
          type: "local",
          internal_id: store.id,
        },
      });
      return newStore;
    } catch (e: unknown) {
      const message = getError(e);
      return ThunkAPI.rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const getLocalStoreFilesAction = createAsyncThunk(
  "database/getLocalStoreFiles",
  async (id: string, ThunkAPI) => {
    try {
      const { dataStore } = (ThunkAPI.getState() as RootState).databaseState;
      const selectedStore = dataStore.find(
        (store: TStoreData) => store.id === id,
      );

      if (!selectedStore) return;

      const response = await getLocalStoreFiles(id as string);

      const updates = getUpdatedStores(dataStore, selectedStore, {
        files: response.data.map((file) => ({
          id: file.id,
          filename: file.filename ?? "",
          storeId: file.vector_store_id,
        })),
      });
      return updates;
    } catch (e: unknown) {
      const message = getError(e);
      return ThunkAPI.rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const createLocalStoreFileAction = createAsyncThunk(
  "database/createLocalStoreFiles",
  async (
    {
      file,
      storeId,
      tempFileId,
    }: { file: File; storeId: string; tempFileId: string },
    { rejectWithValue },
  ) => {
    try {
      const storeFile = await createLocalStoreFile(storeId, file);
      return {
        tempFileId,
        finalFile: {
          id: storeFile.id,
          filename: storeFile.filename,
          storeId,
          isLoading: false,
        },
        storeId,
      };
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue({
        tempFileId,
        error: message,
      });
    }
  },
);

export const updateLocalStoreAction = createAsyncThunk(
  "database/updateLocalStore",
  async (storeName: string, ThunkAPI) => {
    try {
      const { selectedStore, dataStore } = (ThunkAPI.getState() as RootState)
        .databaseState;
      await updateLocalStore(selectedStore?.id as string, {
        name: storeName,
        metadata: {
          type: "local",
          internal_id: selectedStore?.id,
        },
      });
      if (!selectedStore) return;
      const updates = getUpdatedStores(dataStore, selectedStore, {
        name: storeName,
      });

      return updates;
    } catch (e: unknown) {
      const message = getError(e);
      return ThunkAPI.rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const deleteLocalStoreFileAction = createAsyncThunk(
  "database/deleteLocalStoreFile",
  async (fileId: string, ThunkAPI) => {
    try {
      const { selectedStore, dataStore } = (ThunkAPI.getState() as RootState)
        .databaseState;

      if (!selectedStore) return;

      const updatedStoreFileList = selectedStore.files.filter(
        (file: TStoreFile) => file.id !== fileId,
      );
      const updates = getUpdatedStores(dataStore, selectedStore, {
        files: updatedStoreFileList,
      });

      deleteLocalStoreFile(selectedStore?.id, fileId);

      return updates;
    } catch (e: unknown) {
      const message = getError(e);
      return ThunkAPI.rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const deleteLocalStoreAction = createAsyncThunk(
  "database/deleteLocalStore",
  async (id: string, ThunkAPI) => {
    try {
      const { dataStore, selectedStore } = (ThunkAPI.getState() as RootState)
        .databaseState;
      if (!selectedStore) return;
      const updatedStores = dataStore.filter(
        (store: TStoreData) => store.id !== id,
      );
      const nextStore = updatedStores[0];

      ThunkAPI.dispatch(setStoreAction(updatedStores));

      if (!updatedStores[0]) {
        ThunkAPI.dispatch(setSelectedStore(null));
      } else {
        ThunkAPI.dispatch(setSelectedStore(nextStore as TStoreData));
        if (nextStore.metadata.type === "local") {
          await ThunkAPI.dispatch(
            getLocalStoreFilesAction(nextStore?.id as string),
          );
        } else {
          await ThunkAPI.dispatch(getStoreFilesAction(nextStore?.id as string));
        }
      }

      const deleteFilePromises = selectedStore?.files
        ? selectedStore.files.map((file) => deleteLocalStoreFile(id, file.id))
        : [];
      await Promise.all(deleteFilePromises);
      await deleteLocalStore(id);
      return;
    } catch (e: unknown) {
      const message = getError(e);
      return ThunkAPI.rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const getArtifactsFileActions = createAsyncThunk(
  "database/getArtifactsFile",
  async (
    { hash, fileName }: { hash: string; fileName: string },
    { rejectWithValue },
  ) => {
    try {
      return await getFileByHash(hash, fileName);
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);
